import React, { FC, useContext, useState, useRef, useMemo } from 'react'

import { useHistory, useParams } from 'react-router'
import { useLocalStorage } from 'react-use'
import { dataIdFromObject } from '@sketch/graphql-cache'
import { useInView } from 'react-intersection-observer'

import { localStorageKeys } from '@sketch/constants'
import { routes, RouteParams } from '@sketch/modules-common'

import { ReactComponent as PlusIcon } from '@sketch/icons/plus-17'

import {
  Tooltip,
  handleFetchMore,
  ModalContext,
  SelectDropdownButtonItem,
  NewFeatureHighlightPopover,
} from '@sketch/components'
import * as Collapsible from '@radix-ui/react-collapsible'
import { ToastContext } from '@sketch/toasts'

import ProjectsList from '../ProjectsList'
import AddNewProjectModal from '../../modals/AddNewProjectModal'

import { useGetProjects, UseGetProjectsResponse } from '../../operations'

import {
  MenuWrapper,
  ProjectsHeader,
  WarningIcon,
  Error,
  ErrorText,
  RefetchButton,
  StyledSkeletonSidebarList,
  StyledCollapsibleContent,
  StyledTrigger,
  NewFeatureHighlightPopoverStyle,
} from './ProjectsMenu.styles'
import { CollapsibleIcon } from '../../../workspace/components/Sidebar/Sidebar.styles'

import {
  WorkspaceMinimalFragment,
  CreateProjectMutation,
  GetProjectsQuery,
} from '@sketch/gql-types'

import { userIsEditorOrAdmin } from '../../../workspace/utils'

export interface ProjectsMenuProps {
  showWorkspaceSettings?: boolean
  workspace: WorkspaceMinimalFragment
  onLoadCompleted?: (data: GetProjectsQuery) => void
  showDraftsProjects?: boolean
}

interface ProjectsListContainerProps {
  workspace: WorkspaceMinimalFragment
  projectsResponse: UseGetProjectsResponse
  isProjectsListDisabled?: boolean
  isCollapsed?: boolean
  showCreateProjectEditor: boolean
  onBlur: () => void
  onAdd: () => void
  showDraftsProjects?: boolean
}

/**
 * CONSTANTS
 */
const entriesPath = ['workspace', 'projects', 'entries']

/**
 * HELPER COMPONENTS
 */
const ProjectsListContainer: FC<ProjectsListContainerProps> = props => {
  // This is for setting autofocus just after creating a new folder
  const autofocusLastCreated = useRef(false)
  const openedFoldersCache = useMemo(() => new Map<string, boolean>(), [])

  const {
    workspace,
    onAdd,
    isProjectsListDisabled,
    isCollapsed,
    showDraftsProjects,
    projectsResponse,
  } = props

  const isEditorOrAdmin = userIsEditorOrAdmin(workspace)

  const { projectId: selectedProjectId } =
    useParams<RouteParams<'WORKSPACE_PROJECT'>>()

  const {
    projects,
    refetch,
    fetchMore,
    loading,
    error,
    pinnedProjects,
    rootStandardProjects,
    rootDraftProjects,
    projectsById,
  } = projectsResponse

  const handleRefetch = () => {
    refetch()
  }

  // We will hide this when it's collapsed with the new Sidebar
  if (loading && !isCollapsed && !projects) {
    return <StyledSkeletonSidebarList data-testid="spinner" numberOfItems={4} />
  }

  if (error) {
    return (
      <Error>
        <WarningIcon width="24px" height="24px" />
        <ErrorText>Unable to load folders</ErrorText>
        <RefetchButton type="button" onClick={handleRefetch}>
          Try Again
        </RefetchButton>
      </Error>
    )
  }

  if (!projects) {
    return null
  }

  const {
    meta: { after, totalCount },
  } = projects

  const handleLoadMore = handleFetchMore(fetchMore, entriesPath, {
    dataIdFromObject,
    after,
  })

  if (showDraftsProjects) {
    return (
      <ProjectsList
        projectsById={projectsById}
        canLoadMore={after !== null}
        onLoadMore={handleLoadMore}
        projects={rootDraftProjects}
        workspace={workspace}
        autofocusLastCreated={autofocusLastCreated}
        isDisabled={isProjectsListDisabled}
        defaultOpen={id => !!openedFoldersCache.get('drafts|' + id)}
        onOpenChange={(id, open) => {
          openedFoldersCache.set('drafts|' + id, open)
        }}
      />
    )
  }

  if (totalCount === 0) {
    if (isEditorOrAdmin) {
      if (isCollapsed) {
        return null
      }

      return (
        <SelectDropdownButtonItem
          text="Create Folder..."
          icon={PlusIcon}
          onClick={onAdd}
        />
      )
    }

    return (
      <Tooltip
        placement="bottom"
        content="Only Editors and Admins can create new folders"
      >
        <SelectDropdownButtonItem
          text="Create Folder..."
          icon={PlusIcon}
          onClick={onAdd}
          disabled
        />
      </Tooltip>
    )
  }

  const selectedProject = projectsById[selectedProjectId]

  // For the collapsed state, we only want to show standard projects, not
  // drafts (projectsById includes both)
  const shouldShowCollapsedProject =
    isCollapsed && selectedProject?.project?.projectType === 'STANDARD'

  const list = (
    <ProjectsList
      projectsById={projectsById}
      canLoadMore={after !== null}
      onLoadMore={handleLoadMore}
      pinned={shouldShowCollapsedProject ? [] : pinnedProjects}
      projects={
        shouldShowCollapsedProject ? [selectedProject] : rootStandardProjects
      }
      workspace={workspace}
      autofocusLastCreated={autofocusLastCreated}
      isDisabled={isProjectsListDisabled}
      defaultOpen={id => !!openedFoldersCache.get('standard|' + id)}
      onOpenChange={(id, open) => {
        openedFoldersCache.set('standard|' + id, open)
      }}
    />
  )

  return (
    <>
      {shouldShowCollapsedProject ? (
        list
      ) : (
        <StyledCollapsibleContent data-testid="projects-content">
          {list}
        </StyledCollapsibleContent>
      )}
    </>
  )
}

/**
 * MAIN COMPONENTS
 */
export const ProjectsMenu: FC<ProjectsMenuProps> = ({
  workspace,
  onLoadCompleted,
  showDraftsProjects,
}) => {
  const { showModal, hideModal } = useContext(ModalContext)
  const history = useHistory()
  const workspaceId = workspace.identifier
  const isProjectsListDisabled = workspace.status === 'INACTIVE'

  const [ref, isInView] = useInView({ delay: 600 })

  const localStorageKey = localStorageKeys.sidebarSectionProjectsCollapsed
  const [isCollapsedProjectsSection, setIsCollapsedProjectsSection] =
    useLocalStorage(localStorageKey)

  const [showCreateProjectEditor, setShowCreateProject] = useState(false)

  const projectsResponse = useGetProjects({
    workspaceId,
    fetchPolicy: 'cache-and-network',
    shouldInvalidatePrevious: (prev, curr) =>
      prev?.workspaceId !== curr?.workspaceId,
    skip: !workspace,
    onCompleted: onLoadCompleted,
  })

  const { showToast } = useContext(ToastContext)

  const handleOnComplete = (data: CreateProjectMutation) => {
    const { name, identifier: projectId } = data.createWorkspaceProject.project

    showToast(`"${name}" Folder created`)

    history.push(
      routes.WORKSPACE_PROJECT.create({
        projectId,
        workspaceId,
      })
    )

    hideModal()
  }

  const handleOnAdd = () => {
    showModal(AddNewProjectModal, {
      workspace,
      onCompleted: handleOnComplete,
    })
    return
  }

  const handleOnBlur = () => {
    setShowCreateProject(false)
  }

  const handleCollapsibleClick = (e: React.SyntheticEvent) => {
    e.preventDefault()
    setIsCollapsedProjectsSection(!isCollapsedProjectsSection)
  }

  if (showDraftsProjects) {
    return (
      <ProjectsListContainer
        workspace={workspace}
        isProjectsListDisabled={isProjectsListDisabled}
        isCollapsed={Boolean(isCollapsedProjectsSection)}
        onBlur={handleOnBlur}
        showCreateProjectEditor={showCreateProjectEditor}
        onAdd={handleOnAdd}
        projectsResponse={projectsResponse}
        showDraftsProjects
      />
    )
  }

  return (
    <MenuWrapper data-testid="projects" ref={ref}>
      <Collapsible.Root
        className="CollapsibleRoot"
        open={!isCollapsedProjectsSection}
        onOpenChange={
          setIsCollapsedProjectsSection as (isOpen: boolean) => void
        }
      >
        <NewFeatureHighlightPopoverStyle>
          {className => (
            <NewFeatureHighlightPopover
              titleText="Organize your work"
              bodyText="Projects and collections are now folders. Use nested folders to organize your Workspace however you want."
              confirmCopy="Learn more"
              onConfirm={() => {
                // TODO: https://linear.app/sketch/issue/SWEB-626/define-link-for-learn-more-for-the-onboarding-popover
              }}
              localStorageKey="seenNestedFoldersPopover"
              highlightPlacement="sidebar"
              placement="left-start"
              modifiers={[{ name: 'offset', options: { offset: [16, 6] } }]}
              classNamePopper={className}
              show={isInView}
            >
              <ProjectsHeader
                onAdd={handleOnAdd}
                isProjectsListDisabled={isProjectsListDisabled}
                userCanAddProjects={userIsEditorOrAdmin(workspace)}
                collapseButton={
                  <StyledTrigger data-testid="collapse-projects-button" asChild>
                    <CollapsibleIcon
                      isOpen={!isCollapsedProjectsSection}
                      onClick={handleCollapsibleClick}
                    />
                  </StyledTrigger>
                }
              />
            </NewFeatureHighlightPopover>
          )}
        </NewFeatureHighlightPopoverStyle>

        {/* We need to include the Collapsible.Content
            one level inside as we need to handle the switch between
            the active project and the list depending if is collapsed or not */}
        <ProjectsListContainer
          workspace={workspace}
          isCollapsed={Boolean(isCollapsedProjectsSection)}
          onBlur={handleOnBlur}
          showCreateProjectEditor={showCreateProjectEditor}
          onAdd={handleOnAdd}
          projectsResponse={projectsResponse}
          isProjectsListDisabled={isProjectsListDisabled}
        />
      </Collapsible.Root>
    </MenuWrapper>
  )
}
